import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Paperclip, Inbox } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import { usePreferencesStore } from '../../store/preferencesStore';
import { formatHistoryTime } from '../../utils/formatHistoryTime';
import { appDateLocale } from '../../utils/appLocale';
import { senderName, senderInitials, senderColor, senderAddress } from './mailAvatar';
import { spamBand, formatScore } from './spamRules';

export const LIST_FILTERS = ['all', 'unread', 'attachments'];

/** The list filter chips — pure, so the keyboard handler and the view agree. */
export function applyListFilter(messages, filter) {
  if (filter === 'unread') return messages.filter((m) => !m.read);
  if (filter === 'attachments') return messages.filter((m) => (m.attachmentCount || 0) > 0);
  return messages;
}

export function MailAvatar({ sender, size = 36 }) {
  return (
    <span
      className="mailc-avatar"
      data-testid="mail-avatar"
      style={{ width: size, height: size, background: senderColor(sender), fontSize: Math.round(size * 0.36) }}
      aria-hidden="true"
    >
      {senderInitials(sender)}
    </span>
  );
}

/** "Today" / "Yesterday" / a short date, in the APP locale (never the OS one). */
export function dayGroupLabel(value, t, now = new Date()) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return t('mailCatcher.groupToday');
  if (diffDays === 1) return t('mailCatcher.groupYesterday');
  return d.toLocaleDateString(appDateLocale(), { weekday: 'short', day: 'numeric', month: 'short' });
}

/** The row's recipient: one address in mono, or "N recipients". */
export function recipientLabel(to, t) {
  const list = Array.isArray(to) ? to.filter(Boolean) : to ? [to] : [];
  if (list.length === 0) return '';
  if (list.length === 1) return senderAddress(list[0]) || list[0];
  return t('mailCatcher.recipientsCount', { count: list.length });
}

/**
 * The row badge for the spam score (PLAN D4): red `SPAM 5.3` at or above the
 * threshold, an amber `4.6` chip (no word) in the mid band, nothing below.
 * Rows come from `Summary`, which carries only `spamScore`.
 */
export function SpamRowBadge({ score, t }) {
  const band = spamBand(score);
  if (band === 'high') {
    return (
      <span className="mailc-row-spam mailc-row-spam--high" data-testid="mail-row-spam" data-band="high">
        {t('mailCatcher.spam.badge')} {formatScore(score)}
      </span>
    );
  }
  if (band === 'mid') {
    return (
      <span className="mailc-row-spam mailc-row-spam--mid" data-testid="mail-row-spam" data-band="mid">
        {formatScore(score)}
      </span>
    );
  }
  return null;
}

function MailRow({ message, selected, onSelect, timeFormat, t }) {
  const unread = !message.read;
  const attachments = message.attachmentCount || 0;
  const spamScore = message.spamScore ?? 0;
  return (
    <div
      role="option"
      aria-selected={selected}
      data-testid="mail-row"
      data-unread={unread ? 'true' : 'false'}
      className={`mailc-row${selected ? ' mailc-row--selected' : ''}${unread ? ' mailc-row--unread' : ''}`}
      onClick={() => onSelect(message.id)}
    >
      <MailAvatar sender={message.from} size={30} />
      <div className="mailc-row-body">
        <div className="mailc-row-top">
          <span className="mailc-row-sender">{senderName(message.from)}</span>
          {recipientLabel(message.to, t) && (
            <span className="mailc-row-to">
              <ArrowRight size={10} aria-hidden="true" />
              <span className="mailc-row-to-text">{recipientLabel(message.to, t)}</span>
            </span>
          )}
          <span className="mailc-row-time">{formatHistoryTime(message.receivedAt, timeFormat)}</span>
        </div>
        <div className="mailc-row-subject">
          {attachments > 0 && <Paperclip size={12} className="mailc-row-clip" aria-label={t('mailCatcher.attachments')} />}
          <span className="mailc-row-subject-text">{message.subject || t('mailCatcher.noSubject')}</span>
          <SpamRowBadge score={spamScore} t={t} />
        </div>
        <div className="mailc-row-snippet">
          {attachments > 0 && (
            <span className="mailc-row-attachments">
              {attachments === 1 ? t('mailCatcher.attachmentOne') : t('mailCatcher.attachmentMany', { count: attachments })}
              {message.snippet ? ' · ' : ''}
            </span>
          )}
          {message.snippet}
        </div>
      </div>
    </div>
  );
}

/**
 * The message list (left pane): filter chips, day group headers, rows.
 * Keyboard: ↑/↓ move the selection over the VISIBLE rows, Delete/Backspace
 * ask to delete the selected message (the view owns the confirm).
 */
export default function MailCatcherList({
  messages,
  selectedId,
  query,
  filter = 'all',
  port,
  onSelect,
  onFilterChange,
  onDeleteRequest,
}) {
  const { t } = useTranslation();
  const timeFormat = usePreferencesStore((s) => s.timeFormat);
  const listRef = useRef(null);
  const visible = useMemo(() => applyListFilter(messages, filter), [messages, filter]);

  const groups = useMemo(() => {
    const out = [];
    for (const m of visible) {
      const label = dayGroupLabel(m.receivedAt, t);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(m);
      else out.push({ label, items: [m] });
    }
    return out;
  }, [visible, t]);

  const onKeyDown = useCallback(
    (e) => {
      if (!visible.length) return;
      const idx = visible.findIndex((m) => m.id === selectedId);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        onSelect(visible[Math.min(visible.length - 1, idx + 1)].id);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        onSelect(visible[Math.max(0, idx <= 0 ? 0 : idx - 1)].id);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId != null) {
        e.preventDefault();
        onDeleteRequest(selectedId);
      }
    },
    [visible, selectedId, onSelect, onDeleteRequest]
  );

  useEffect(() => {
    if (selectedId == null || !listRef.current) return;
    const el = listRef.current.querySelector('.mailc-row--selected');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const filterLabel = { all: 'filterAll', unread: 'filterUnread', attachments: 'attachments' };
  const chips = (
    <div className="mailc-filters" role="radiogroup" aria-label={t('mailCatcher.filters')}>
      {LIST_FILTERS.map((key) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={filter === key}
          className={`mailc-filter${filter === key ? ' mailc-filter--active' : ''}`}
          onClick={() => onFilterChange && onFilterChange(key)}
        >
          {t(`mailCatcher.${filterLabel[key]}`)}
        </button>
      ))}
    </div>
  );

  if (messages.length === 0) {
    if (query) {
      return (
        <div className="mailc-list-empty" data-testid="mail-list-empty">
          <span className="mailc-list-empty-title">{t('mailCatcher.noMatches')}</span>
        </div>
      );
    }
    return (
      <div className="mailc-list-empty" data-testid="mail-list-empty">
        <div className="mailc-list-empty-icon">
          <Inbox size={22} />
        </div>
        <span className="mailc-list-empty-title">{t('mailCatcher.empty')}</span>
        <span className="mailc-list-empty-sub">{t('mailCatcher.emptyHint')}</span>
        <div className="mailc-howto">
          <div className="mailc-howto-title">{t('mailCatcher.howToTitle')}</div>
          <div className="mailc-howto-body">{t('mailCatcher.howToBody', { port })}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mailc-list-pane">
      {chips}
      {visible.length === 0 ? (
        <div className="mailc-list-empty" data-testid="mail-list-empty">
          <span className="mailc-list-empty-title">{t('mailCatcher.noFilterMatches')}</span>
        </div>
      ) : (
        <OsScroll className="mailc-list-scroll">
          <div
            ref={listRef}
            role="listbox"
            aria-label={t('mailCatcher.messageCount', { count: visible.length })}
            tabIndex={0}
            className="mailc-list"
            onKeyDown={onKeyDown}
          >
            {groups.map((g) => (
              <div key={g.label} className="mailc-group" data-testid="mail-group">
                <div className="mailc-group-label">{g.label}</div>
                {g.items.map((m) => (
                  <MailRow key={m.id} message={m} selected={m.id === selectedId} onSelect={onSelect} timeFormat={timeFormat} t={t} />
                ))}
              </div>
            ))}
          </div>
        </OsScroll>
      )}
    </div>
  );
}
